import { Text } from '@/ui-kit'
import { Spinner } from '@/ui-kit/components/Spinner'
import { clsx } from 'clsx'
import { ConnectionState } from 'livekit-client'
import { Mic, MicOff, PhoneOff, RotateCcw } from 'lucide-react'
import { useEffect, useRef, useState } from 'react'
import { leaveSession } from '../../api/conversations'
import { useConversationLogger } from '../../hooks/useConversationLogger'
import type { LiveKitParticipant } from '../../hooks/useLiveKit'
import { EndConversationDialog } from './EndConversationDialog'
import { ParticipantCircles } from './ParticipantCircles'
import { RecordingStartCountdown } from './RecordingStartCountdown'
import { RestartVoteDialog, type RestartVoteState } from './RestartVoteDialog'

export type RestartState = RestartVoteState

interface SessionCallProps {
  sessionId: string
  minDurationSec: number
  maxDurationSec: number
  callStartedAt?: string
  topic?: string
  instruction?: string
  onEnded: (durationSec: number) => void
  connectionState: ConnectionState
  participants: LiveKitParticipant[]
  isMuted: boolean
  toggleMute: () => void
  restart: RestartState
  restartCount: number
  restartPending: boolean
  onRequestRestart: () => void
  onVoteRestart: (vote: boolean) => void
  recordingStartsAt: string | null
  onRestartCountdownComplete: () => void
}

export function SessionCall({
  sessionId,
  minDurationSec,
  maxDurationSec,
  callStartedAt,
  topic,
  instruction,
  onEnded,
  connectionState,
  participants,
  isMuted,
  toggleMute,
  restart,
  restartCount,
  restartPending,
  onRequestRestart,
  onVoteRestart,
  recordingStartsAt,
  onRestartCountdownComplete
}: SessionCallProps) {
  const log = useConversationLogger()

  const [elapsedSec, setElapsedSec] = useState(() =>
    callStartedAt ? Math.max(0, Math.floor((Date.now() - new Date(callStartedAt).getTime()) / 1000)) : 0
  )
  const [ending, setEnding] = useState(false)
  const [confirmOpen, setConfirmOpen] = useState(false)
  const startTimeRef = useRef(callStartedAt ? new Date(callStartedAt).getTime() : Date.now())
  const wasConnectedRef = useRef(false)
  const autoEndTriggeredRef = useRef(false)
  const handleEndRef = useRef<() => void>(() => {})
  const elapsedSecRef = useRef(0)

  const [restartCountdown, setRestartCountdown] = useState<number | null>(null)
  const restartCountdownCompleteRef = useRef(onRestartCountdownComplete)
  restartCountdownCompleteRef.current = onRestartCountdownComplete

  useEffect(() => {
    if (!recordingStartsAt) {
      setRestartCountdown(null)
      return
    }
    const target = new Date(recordingStartsAt).getTime()
    const tick = () => {
      const remaining = Math.ceil((target - Date.now()) / 1000)
      if (remaining <= 0) {
        setRestartCountdown(null)
        restartCountdownCompleteRef.current()
        return
      }
      setRestartCountdown(remaining)
    }
    tick()
    const interval = setInterval(tick, 200)
    return () => clearInterval(interval)
  }, [recordingStartsAt])

  const isRestartCountingDown = restartCountdown !== null && restartCountdown > 0

  useEffect(() => {
    log('info', 'SessionCall mounted', {
      sessionId,
      minDurationSec,
      maxDurationSec
    })
  }, [log, sessionId, minDurationSec, maxDurationSec])

  useEffect(() => {
    if (connectionState === ConnectionState.Connected) {
      wasConnectedRef.current = true
    }
  }, [connectionState])

  useEffect(() => {
    if (restartCount === 0) return
    startTimeRef.current = Date.now()
    setElapsedSec(0)
    elapsedSecRef.current = 0
    autoEndTriggeredRef.current = false
    log('session', 'Timer reset for restart', { restartCount })
  }, [restartCount, log])

  // Sync startTimeRef when callStartedAt prop updates (e.g., after server sync)
  useEffect(() => {
    if (!callStartedAt) return
    const ts = new Date(callStartedAt).getTime()
    if (ts === startTimeRef.current) return
    // After a restart, the local timer resets at countdown completion while the
    // server sets callStartedAt 1-2s later. Skip small corrections (<5s) to
    // avoid a visible timer jump; only sync for large drifts.
    const drift = Math.abs(ts - startTimeRef.current)
    if (drift < 5000) return
    startTimeRef.current = ts
    const sec = Math.max(0, Math.floor((Date.now() - ts) / 1000))
    elapsedSecRef.current = sec
    setElapsedSec(sec)
  }, [callStartedAt])

  useEffect(() => {
    const interval = setInterval(() => {
      const sec = Math.floor((Date.now() - startTimeRef.current) / 1000)
      elapsedSecRef.current = sec
      setElapsedSec(sec)
    }, 1000)
    return () => clearInterval(interval)
  }, [])

  // Detect room disconnection (other participant ended the session)
  useEffect(() => {
    if (wasConnectedRef.current && connectionState === ConnectionState.Disconnected && !ending) {
      log('session', 'Room disconnected (other participant ended session)', {
        elapsedSec: elapsedSecRef.current,
        connectionState
      })
      onEnded(elapsedSecRef.current)
    }
  }, [connectionState, ending, onEnded, log])

  useEffect(() => {
    if (elapsedSec >= maxDurationSec && !ending && !autoEndTriggeredRef.current) {
      autoEndTriggeredRef.current = true
      log('session', `Max duration reached (${maxDurationSec}s), auto-ending`)
      handleEndRef.current()
    }
  }, [elapsedSec, maxDurationSec, ending, log])

  const canEnd = elapsedSec >= minDurationSec
  const minReached = elapsedSec >= minDurationSec
  const endingCountdownSec = maxDurationSec > 0 ? maxDurationSec - elapsedSec : null
  const showEndingCountdown = endingCountdownSec !== null && endingCountdownSec <= 30 && endingCountdownSec > 0

  const formatTime = (s: number) => `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`

  const handleEnd = () => {
    log('api', `POST /v1/conversations/sessions/${sessionId}/leave`, {
      elapsedSec,
      minDurationSec
    })
    setEnding(true)
    setConfirmOpen(false)
    onEnded(elapsedSec)
    leaveSession(sessionId).catch((err) => {
      const resp = (err as { response?: { status?: number; data?: { message?: string } } }).response
      if (resp?.status !== 400) {
        log('error', `Leave API failed: ${resp?.data?.message || 'leave'}`)
      }
    })
  }
  handleEndRef.current = handleEnd

  if (connectionState === ConnectionState.Connecting) {
    return (
      <div style={{ animation: 'nectar-slide-up 0.4s ease-out' }}>
        <div className="flex flex-col items-center gap-6 rounded-2xl border border-solid border-gray-100 px-8 py-10">
          <Spinner />
          <Text type="body2-regular" className="text-gray-90">
            Connecting to room...
          </Text>
        </div>
      </div>
    )
  }

  const showVoteModal = restart.activeRestartId !== null

  return (
    <div style={{ animation: 'nectar-slide-up 0.4s ease-out' }}>
      <div className="flex flex-col items-center gap-6 rounded-2xl border border-solid border-gray-100 px-8 py-10">
        {isRestartCountingDown ? (
          <RecordingStartCountdown secondsRemaining={restartCountdown ?? 0} />
        ) : (
          <>
            {instruction && (
              <Text
                type="body2-regular"
                className="text-gray-90 m-auto block rounded-xl border border-solid border-gray-100 p-2 text-center"
              >
                {instruction}
              </Text>
            )}
            {topic && (
              <span className="text-h5 block text-center leading-[120%] whitespace-pre-line">
                &ldquo;{topic}&rdquo;
              </span>
            )}
            <ParticipantCircles participants={participants} isMuted={isMuted} toggleMute={toggleMute} />
            <div className="flex flex-col items-center gap-1">
              <span className="font-mono text-3xl font-semibold tabular-nums">{formatTime(elapsedSec)}</span>
              {restartCount > 0 && (
                <Text type="body3-regular" className="text-gray-80">
                  Restarted {restartCount} {restartCount === 1 ? 'time' : 'times'}
                </Text>
              )}
              <Text type="body3-regular" className={minReached ? 'text-green' : 'text-gray-80'}>
                {minReached ? 'Minimum duration reached' : `Min: ${formatTime(minDurationSec)}`}
              </Text>
              {maxDurationSec > 0 && (
                <Text
                  type="body3-regular"
                  className={clsx(maxDurationSec - elapsedSec <= 60 ? 'text-red font-medium' : 'text-gray-80')}
                >
                  {elapsedSec >= maxDurationSec ? "Time's up" : `${formatTime(maxDurationSec - elapsedSec)} remaining`}
                </Text>
              )}
            </div>

            <div className="flex items-center gap-6">
              <button
                type="button"
                onClick={onRequestRestart}
                disabled={restartPending || restart.activeRestartId !== null}
                title={
                  restartPending || restart.activeRestartId !== null
                    ? 'A restart vote is in progress'
                    : 'Restart recording'
                }
                className={clsx(
                  'flex size-14 items-center justify-center rounded-full transition-colors',
                  restartPending || restart.activeRestartId !== null
                    ? 'text-gray-80 cursor-not-allowed bg-gray-100 opacity-50'
                    : 'bg-orange/20 text-orange hover:bg-orange/30'
                )}
              >
                <RotateCcw className="size-6" />
              </button>

              <button
                type="button"
                onClick={toggleMute}
                className={clsx(
                  'flex size-14 items-center justify-center rounded-full transition-colors',
                  isMuted ? 'bg-red text-white' : 'text-gray-80 bg-gray-100 hover:bg-gray-100/80'
                )}
              >
                {isMuted ? <MicOff className="size-6" /> : <Mic className="size-6" />}
              </button>

              <button
                type="button"
                onClick={() => setConfirmOpen(true)}
                disabled={!canEnd || ending}
                title={!canEnd ? `Wait ${formatTime(minDurationSec - elapsedSec)} more` : 'End conversation'}
                className={clsx(
                  'flex size-16 items-center justify-center rounded-full transition-all',
                  canEnd && !ending
                    ? 'bg-red text-white hover:brightness-110 active:scale-95'
                    : 'text-gray-80 cursor-not-allowed bg-gray-100 opacity-50'
                )}
              >
                <PhoneOff className="size-7" />
              </button>
            </div>
          </>
        )}
      </div>
      {showEndingCountdown && !isRestartCountingDown && (
        <div className="pointer-events-none fixed inset-0 z-40 flex items-center justify-center bg-black/70">
          <div className="flex flex-col items-center gap-3">
            <div className="bg-red flex size-24 items-center justify-center rounded-full">
              <span className="text-5xl font-bold text-white tabular-nums">{endingCountdownSec}</span>
            </div>
            <Text type="body2-regular" className="text-red font-medium">
              Conversation ending...
            </Text>
          </div>
        </div>
      )}
      <EndConversationDialog open={confirmOpen} onOpenChange={setConfirmOpen} onConfirmEnd={handleEnd} />
      <RestartVoteDialog
        open={showVoteModal}
        restart={restart}
        restartPending={restartPending}
        onVoteRestart={onVoteRestart}
      />
    </div>
  )
}
